/**
 * US individual income distribution used to answer "what percent of Americans
 * earn this?".
 *
 * This is a different question from the OEWS occupation percentiles that power
 * salary pages. OEWS answers "where do you rank among people doing your job";
 * this answers "where do you rank among all earners". Both are shown together
 * on /income-percentile because Reddit and search traffic conflates them
 * constantly.
 *
 * Breakpoints are drawn from published Census CPS ASEC individual earnings
 * tables. They are static constants refreshed annually when new CPS data lands,
 * not an API - there is no BLS OEWS series for an all-workers income
 * distribution.
 */

export interface IncomeBreakpoint {
    /** Percentile of earners at or below this income. */
    percentile: number;
    /** Annual pre-tax individual income in USD. */
    income: number;
}

/** Vintage of the underlying CPS tables, surfaced in the UI for provenance. */
export const INCOME_DATA_YEAR = 2024;

/**
 * All individual earners, including part-year and part-time workers. This is
 * the distribution most "what percentile is $X" answers online refer to, and
 * the one the viral dating-standards calculators use for their income filter.
 */
export const US_INDIVIDUAL_INCOME_PERCENTILES: IncomeBreakpoint[] = [
    { percentile: 5, income: 8_000 },
    { percentile: 10, income: 15_000 },
    { percentile: 20, income: 23_000 },
    { percentile: 25, income: 28_000 },
    { percentile: 30, income: 32_000 },
    { percentile: 40, income: 38_000 },
    { percentile: 50, income: 45_000 },
    { percentile: 60, income: 53_000 },
    { percentile: 70, income: 65_000 },
    { percentile: 75, income: 75_000 },
    { percentile: 80, income: 88_000 },
    { percentile: 90, income: 125_000 },
    { percentile: 95, income: 175_000 },
    { percentile: 99, income: 400_000 },
    { percentile: 99.9, income: 1_500_000 },
];

/**
 * Full-time, year-round workers only. Consistently higher than the all-earners
 * cut because it excludes part-year and part-time earnings, which is why the
 * same salary can be quoted at two very different percentiles.
 */
export const US_FULLTIME_INCOME_PERCENTILES: IncomeBreakpoint[] = [
    { percentile: 5, income: 22_000 },
    { percentile: 10, income: 28_000 },
    { percentile: 20, income: 36_000 },
    { percentile: 25, income: 40_000 },
    { percentile: 30, income: 44_000 },
    { percentile: 40, income: 52_000 },
    { percentile: 50, income: 61_000 },
    { percentile: 60, income: 70_000 },
    { percentile: 70, income: 83_000 },
    { percentile: 75, income: 92_000 },
    { percentile: 80, income: 105_000 },
    { percentile: 90, income: 150_000 },
    { percentile: 95, income: 200_000 },
    { percentile: 99, income: 450_000 },
    { percentile: 99.9, income: 1_600_000 },
];

/** Highest percentile the tables can support without inventing a tail. */
const MAX_PERCENTILE = 99.9;

/**
 * Estimates the percentile of earners at or below `annualIncome` by linear
 * interpolation between published breakpoints.
 *
 * Incomes below the lowest breakpoint scale linearly to zero. Incomes above the
 * highest breakpoint clamp at 99.9 rather than extrapolating, because the top
 * tail is heavy enough that a straight-line estimate is meaningless there.
 */
export function estimateIncomePercentile(
    annualIncome: number,
    distribution: IncomeBreakpoint[] = US_INDIVIDUAL_INCOME_PERCENTILES,
): number {
    if (!Number.isFinite(annualIncome) || annualIncome <= 0) return 0;

    const first = distribution[0];
    if (annualIncome <= first.income) {
        return Number(((annualIncome / first.income) * first.percentile).toFixed(1));
    }

    for (let i = 1; i < distribution.length; i += 1) {
        const lower = distribution[i - 1];
        const upper = distribution[i];
        if (annualIncome <= upper.income) {
            const span = upper.income - lower.income;
            const ratio = span > 0 ? (annualIncome - lower.income) / span : 0;
            const percentile = lower.percentile + ratio * (upper.percentile - lower.percentile);
            return Number(percentile.toFixed(1));
        }
    }

    return MAX_PERCENTILE;
}

/**
 * Share of earners making at least `annualIncome`, i.e. the complement of the
 * percentile. This is the figure people actually quote in arguments ("only 18%
 * of men earn six figures").
 */
export function shareEarningAtLeast(
    annualIncome: number,
    distribution: IncomeBreakpoint[] = US_INDIVIDUAL_INCOME_PERCENTILES,
): number {
    return Number((100 - estimateIncomePercentile(annualIncome, distribution)).toFixed(1));
}

/**
 * Percentiles read as ordinals in prose ("the 83rd percentile"), so they are
 * rounded to a whole number for display. The fractional precision from
 * interpolation is false confidence anyway.
 */
export function formatPercentileOrdinal(percentile: number): string {
    const n = Math.round(percentile);
    const lastTwo = n % 100;
    if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
    switch (n % 10) {
        case 1:
            return `${n}st`;
        case 2:
            return `${n}nd`;
        case 3:
            return `${n}rd`;
        default:
            return `${n}th`;
    }
}

/**
 * "Top N%" label for a percentile, or null when the income is not in a tier
 * worth badging. Thresholds match the breakpoints so the badge never disagrees
 * with the reference table.
 */
export function getTopPercentLabel(percentile: number): string | null {
    if (percentile >= 99) return 'Top 1%';
    if (percentile >= 95) return 'Top 5%';
    if (percentile >= 90) return 'Top 10%';
    if (percentile >= 75) return 'Top 25%';
    return null;
}

/** Percentile rows shown in the reference table on /income-percentile. */
export const REFERENCE_PERCENTILES = [10, 25, 50, 75, 90, 95, 99];

export interface ReferenceRow {
    percentile: number;
    label: string;
    individual: number;
    fullTime: number;
}

/**
 * Joins the two distributions into the side-by-side reference table. Both cuts
 * are shown together because quoting one without the other is the single most
 * common source of disagreement in income-percentile threads.
 */
export function getReferenceTable(): ReferenceRow[] {
    const fullTimeByPercentile = new Map(
        US_FULLTIME_INCOME_PERCENTILES.map((b) => [b.percentile, b.income]),
    );

    return REFERENCE_PERCENTILES.map((percentile) => {
        const individual = US_INDIVIDUAL_INCOME_PERCENTILES.find(
            (b) => b.percentile === percentile,
        );
        return {
            percentile,
            label:
                percentile === 50
                    ? '50th (median)'
                    : formatPercentileOrdinal(percentile),
            individual: individual ? individual.income : 0,
            fullTime: fullTimeByPercentile.get(percentile) ?? 0,
        };
    });
}
